import { useState, useEffect, useCallback } from 'react';
import {
  X,
  AlertTriangle,
  Gift,
  Image as ImageIcon,
  CheckCheck,
  ShoppingBag,
} from 'lucide-react';
import API_BASE_URL from '../../config';
import './NotificationCenter.css';


/* ─── Helper: get auth token ─── */
const getAuthToken = () => {
  try {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) return adminToken;

    const stored = JSON.parse(localStorage.getItem('accessToken') || '{}');
    return stored.token || '';
  } catch {
    return '';
  }
};

/* ─── Helper: format date label from ISO timestamp ─── */
const getDateLabel = (isoString) => {
  if (!isoString) return 'Earlier';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return 'Earlier';
};

/* ─── Helper: format relative time ─── */
const getRelativeTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/* ─── Icon renderer per type ─── */
const NotifIcon = ({ type, thumbnail }) => {
  if ((type === 'gymfeed' || type === 'store') && thumbnail) {
    return (
      <img
        src={thumbnail}
        alt=""
        className="w-[42px] h-[42px] rounded-xl object-cover flex-shrink-0 border border-[#2b2b2b]"
      />
    );
  }

  if (type === 'gymfeed') {
    return (
      <div className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#1a2a10] to-[#1f3316] border border-[rgba(108,216,0,0.2)]">
        <ImageIcon size={18} color="#6CD800" strokeWidth={1.8} />
      </div>
    );
  }

  if (type === 'membership') {
    return (
      <div className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#2a2210] to-[#332b16] border border-[rgba(255,180,0,0.2)]">
        <AlertTriangle size={18} color="#FFB400" strokeWidth={1.8} />
      </div>
    );
  }

  if (type === 'rewards') {
    return (
      <div className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#102a1a] to-[#163322] border border-[rgba(108,216,0,0.25)]">
        <Gift size={18} color="#6CD800" strokeWidth={1.8} />
      </div>
    );
  }

  if (type === 'store') {
    return (
      <div className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#10242a] to-[#162e33] border border-[rgba(0,180,216,0.25)]">
        <ShoppingBag size={18} color="#00B4D8" strokeWidth={1.8} />
      </div>
    );
  }

  return null;
};

/* ─── Main Component ─── */
const NotificationCenter = ({ isOpen, onClose, onNavigate, username, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Report unread count to parent whenever it changes
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, onUnreadCountChange]);

  const fetchNotifications = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/notification`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const mapped = (data || []).map((n) => ({
          id: n.id,
          type: n.type,
          title: n.title,
          body: n.body,
          thumbnail: n.thumbnail || null,
          targetMenu: n.targetMenu || null,
          read: n.read,
          time: getRelativeTime(n.createdAt),
          date: getDateLabel(n.createdAt),
        }));
        setNotifications(mapped);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // 1. Fetch on mount / username change (silent fetch for badge count)
  useEffect(() => {
    if (username) {
      fetchNotifications(false);
    }
  }, [username, fetchNotifications]);

  // 2. Fetch on panel open (refresh with loading spinner)
  useEffect(() => {
    if (isOpen && username) {
      fetchNotifications(true);
    }
  }, [isOpen, username, fetchNotifications]);

  // ─── Mark All As Read ───
  const markAllAsRead = useCallback(async () => {
    // Optimistic update: immediately mark all as read in UI
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

    // Send PATCH to backend
    if (username) {
      try {
        await fetch(`${API_BASE_URL}/api/notification/mark-all-read`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
      } catch (error) {
        console.error('Failed to mark notifications as read on server:', error);
        // Optimistic update already applied — UI stays clean even if API fails
      }
    }
  }, [username]);

  // ─── Mark Single As Read + Navigate ───
  const handleCardClick = useCallback(async (notif) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
    );

    // Send PATCH for single notification
    if (username) {
      try {
        await fetch(`${API_BASE_URL}/api/notification/mark-read/${notif.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getAuthToken()}`,
          },
        });
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Navigate to the relevant section
    if (onNavigate) {
      if (notif.targetMenu) {
        onNavigate(notif.targetMenu);
      } else {
        const text = `${notif.title || ''} ${notif.body || ''}`.toLowerCase();
        if (text.includes('freeze')) {
          onNavigate('FreezeRequests');
        } else if (text.includes('redemption') || text.includes('redeem')) {
          onNavigate('Redemptions');
        } else if (text.includes('expense')) {
          onNavigate('Expenses');
        } else if (text.includes('renewal') || text.includes('expir')) {
          onNavigate('RenewalsDue');
        } else if (text.includes('birthday')) {
          onNavigate('Birthdays');
        }
      }
    }
    onClose();
  }, [username, onNavigate, onClose]);

  if (!isOpen) return null;

  // Group by date
  const dateGroups = [];
  const seen = new Set();
  notifications.forEach((n) => {
    if (!seen.has(n.date)) {
      seen.add(n.date);
      dateGroups.push(n.date);
    }
  });

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/45 backdrop-blur-sm z-[80]"
        style={{ animation: 'notif-fade-in 0.2s ease-out' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 w-[400px] max-w-full h-screen bg-[#141414] border-l border-[#2b2b2b] z-[90] flex flex-col"
        style={{ animation: 'notif-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[#222]">
          <div className="flex items-center gap-2.5">
            <span className="text-lg font-bold text-[#f0f0f0] tracking-tight">Notifications</span>
            {unreadCount > 0 && (
              <span className="bg-[#6CD800] text-[#0a0a0a] text-[0.7rem] font-bold px-2 py-0.5 rounded-full leading-snug">
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                className="bg-transparent border border-[#333] text-[#999] text-xs px-3.5 py-1.5 rounded-lg cursor-pointer transition-all duration-200 whitespace-nowrap hover:bg-[#1e1e1e] hover:text-[#6CD800] hover:border-[#6CD800]"
                onClick={markAllAsRead}
              >
                <CheckCheck size={13} className="inline mr-1 align-middle" />
                Mark all read
              </button>
            )}
            <button
              className="bg-[#1e1e1e] border border-[#2b2b2b] text-[#888] w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 ml-2 flex-shrink-0 hover:bg-[#2b2b2b] hover:text-[#f0f0f0]"
              onClick={onClose}
              aria-label="Close notifications"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-thin scrollbar-thumb-[#333] scrollbar-track-transparent">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-[#6CD800] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            /* ─── Empty State ─── */
            <div className="flex flex-col items-center justify-center py-10">
              <span className="text-gray-500 text-sm">No new notifications.</span>
            </div>
          ) : (
            dateGroups.map((date) => (
              <div key={date}>
                {/* Date divider */}
                <div className="flex items-center gap-3 mt-4 mb-2.5 px-1">
                  <span className="text-[0.72rem] font-semibold text-[#666] uppercase tracking-wider whitespace-nowrap">
                    {date}
                  </span>
                  <div className="flex-1 h-px bg-[#222]" />
                </div>

                {/* Cards */}
                {notifications
                  .filter((n) => n.date === date)
                  .map((notif, idx) => (
                    <div
                      key={notif.id}
                      className={`
                        flex items-start gap-3.5 p-3.5 rounded-xl border mb-2 cursor-pointer
                        transition-all duration-200 relative
                        before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3
                        before:w-[3px] before:rounded-r before:opacity-0 before:transition-opacity before:duration-200
                        hover:translate-x-0.5 hover:before:opacity-100 active:scale-[0.995]
                        ${notif.read
                          ? 'bg-[#1a1a1a] border-[#222] opacity-60 hover:opacity-85 hover:bg-[#1f1f1f] hover:border-[#333]'
                          : 'bg-[#1c1f1a] border-[rgba(108,216,0,0.15)] before:bg-[#6CD800] hover:bg-[#1f1f1f] hover:border-[#333]'
                        }
                      `}
                      style={{ animation: `notif-card-appear 0.35s ease-out ${idx * 0.06}s both` }}
                      onClick={() => handleCardClick(notif)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCardClick(notif);
                      }}
                    >
                      <NotifIcon type={notif.type} thumbnail={notif.thumbnail} />

                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#e8e8e8] mb-0.5 leading-snug">
                          {notif.title}
                        </div>
                        <div className="text-xs text-[#888] leading-relaxed line-clamp-2">
                          {notif.body}
                        </div>
                        <div className="text-[0.68rem] text-[#555] mt-1.5 flex items-center gap-1.5">
                          {!notif.read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#6CD800] flex-shrink-0" />
                          )}
                          {notif.time}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationCenter;
